import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateShoreDepartmentRequest,
    ShoreDepartment,
    ShoreDepartmentsRequest,
    ShoreDepartmentsResponse,
    UpdateShoreDepartmentRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-departments.api.ts';
import {
    createApiShoreDepartment,
    deleteApiShoreDepartment,
    getApiShoreDepartments,
    updateApiShoreDepartment,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-departments.api.ts';
import { extractError } from '@utils/slice.ts';

export interface ShoreDepartmentsError {
    message?: string;
    [k: string]: any;
}

export interface ShoreDepartmentsState {
    error: ShoreDepartmentsError | null;

    commonShoreDepartments: ShoreDepartmentsResponse | null;
    shoreDepartments: ShoreDepartment[];
    pagination: CommonTablePaginatedResponse;

    updatedShoreDepartment: ShoreDepartment | null;
    currentGetRequestId: string | null;

    isGetShoreDepartmentsPending: boolean;
    isCreateShoreDepartmentPending: boolean;
    isUpdateShoreDepartmentPending: boolean;
    isDeleteShoreDepartmentPending: boolean;
}

const initialState: ShoreDepartmentsState = {
    error: null,

    commonShoreDepartments: null,
    shoreDepartments: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedShoreDepartment: null,
    currentGetRequestId: null,

    isGetShoreDepartmentsPending: false,
    isCreateShoreDepartmentPending: false,
    isUpdateShoreDepartmentPending: false,
    isDeleteShoreDepartmentPending: false,
};

export const getShoreDepartments = createAsyncThunk<
    ShoreDepartmentsResponse,
    { clientId: number; tenantAlias: string; payload: ShoreDepartmentsRequest },
    { rejectValue: ShoreDepartmentsError }
>('adminCenterShoreDepartments/getShoreDepartments', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiShoreDepartments(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createShoreDepartment = createAsyncThunk<
    ShoreDepartment,
    { clientId: number; tenantAlias: string; payload: CreateShoreDepartmentRequest },
    { rejectValue: ShoreDepartmentsError }
>('adminCenterShoreDepartments/createShoreDepartment', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiShoreDepartment(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreDepartment = createAsyncThunk<
    ShoreDepartment,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateShoreDepartmentRequest },
    { rejectValue: ShoreDepartmentsError }
>('adminCenterShoreDepartments/updateShoreDepartment', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiShoreDepartment(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreDepartment = createAsyncThunk<
    ShoreDepartment,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: ShoreDepartmentsError }
>('adminCenterShoreDepartments/deleteShoreDepartment', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiShoreDepartment(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const shoreDepartmentsSlice = createSlice({
    name: 'adminCenterShoreDepartments',
    initialState,
    reducers: {
        resetShoreDepartmentsState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getShoreDepartments.pending, (state, action) => {
            state.isGetShoreDepartmentsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getShoreDepartments.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreDepartmentsPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonShoreDepartments = action.payload;
            state.shoreDepartments = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getShoreDepartments.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetShoreDepartmentsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createShoreDepartment.pending, (state) => {
            state.isCreateShoreDepartmentPending = true;
            state.error = null;
        });
        builder.addCase(createShoreDepartment.fulfilled, (state, action) => {
            state.isCreateShoreDepartmentPending = false;
            state.updatedShoreDepartment = action.payload;
        });
        builder.addCase(createShoreDepartment.rejected, (state, action) => {
            state.isCreateShoreDepartmentPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateShoreDepartment.pending, (state) => {
            state.isUpdateShoreDepartmentPending = true;
            state.error = null;
        });
        builder.addCase(updateShoreDepartment.fulfilled, (state, action) => {
            state.isUpdateShoreDepartmentPending = false;
            state.updatedShoreDepartment = action.payload;
        });
        builder.addCase(updateShoreDepartment.rejected, (state, action) => {
            state.isUpdateShoreDepartmentPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteShoreDepartment.pending, (state) => {
            state.isDeleteShoreDepartmentPending = true;
            state.error = null;
        });
        builder.addCase(deleteShoreDepartment.fulfilled, (state, action) => {
            state.isDeleteShoreDepartmentPending = false;
            state.updatedShoreDepartment = action.payload;
            state.shoreDepartments = state.shoreDepartments.filter((d) => d.id !== action.payload.id);
        });
        builder.addCase(deleteShoreDepartment.rejected, (state, action) => {
            state.isDeleteShoreDepartmentPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { shoreDepartmentResponse } = action.payload;

            if (!shoreDepartmentResponse) return;

            const { data, pagination } = shoreDepartmentResponse;
            state.commonShoreDepartments = shoreDepartmentResponse;
            state.shoreDepartments = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetShoreDepartmentsState } = shoreDepartmentsSlice.actions;
export default shoreDepartmentsSlice.reducer;
